import React, { useCallback, useEffect, useRef } from 'react';

import { useAppSelector } from '@verantos-dev/app-state-base';
import {
  selectResearchAssistantIsWaitingForResponse,
  selectResearchAssistantMessages,
} from '@verantos-dev/app-state-research-assistant';

export const useScrollMessageIntoView = () => {
  const messagesEndRef = React.useRef<null | HTMLDivElement>(null);
  const chatWindowRef = React.useRef<HTMLDivElement>(null);

  const messages = useAppSelector(selectResearchAssistantMessages);
  const isWaiting = useAppSelector(selectResearchAssistantIsWaitingForResponse);
  const prevMessageCountRef = useRef(messages?.length ?? 0);
  const wasWaitingRef = useRef(false);
  const hasScrolledToUserMessageRef = useRef(false);

  /**
   * Scroll so the sentinel div (end of real content, before the padding)
   * sits at the bottom of the visible area.
   */
  const scrollToBottom = useCallback(() => {
    const container = chatWindowRef.current;
    if (!container) return;
    const scrollContainer = container.parentElement;
    const sentinel = messagesEndRef.current;
    if (!scrollContainer || !sentinel) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const sentinelRect = sentinel.getBoundingClientRect();
    const targetScrollTop =
      sentinelRect.bottom -
      containerRect.top +
      scrollContainer.scrollTop -
      scrollContainer.clientHeight;
    scrollContainer.scrollTo?.({ top: Math.max(0, targetScrollTop), behavior: 'smooth' });
  }, []);

  /**
   * Scroll so the latest user message is at the top of the scroll area.
   * The permanent 50vh padding provides the scroll room.
   */
  const scrollToLatestUserMessage = useCallback(() => {
    const container = chatWindowRef.current;
    if (!container) return;

    const scrollContainer = container.parentElement;
    if (!scrollContainer) return;

    // Find the last user message using the stable data attribute
    const userMessages = container.querySelectorAll('[data-message-role="user"]');
    const targetElement = userMessages[userMessages.length - 1] as HTMLElement | undefined;
    if (!targetElement) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();
    const targetScrollTop = targetRect.top - containerRect.top + scrollContainer.scrollTop;
    scrollContainer.scrollTo?.({ top: targetScrollTop, behavior: 'smooth' });
  }, []);

  // Track processing state transitions
  useEffect(() => {
    if (isWaiting && !wasWaitingRef.current) {
      hasScrolledToUserMessageRef.current = false;
    } else if (!isWaiting && wasWaitingRef.current) {
      // Processing finished — scroll to bottom to show response
      scrollToBottom();
    }
    wasWaitingRef.current = !!isWaiting;
  }, [isWaiting, scrollToBottom]);

  // Scroll when new messages are added.
  useEffect(() => {
    const currentCount = messages?.length ?? 0;
    if (currentCount !== prevMessageCountRef.current) {
      prevMessageCountRef.current = currentCount;
      requestAnimationFrame(() => {
        if (isWaiting && !hasScrolledToUserMessageRef.current) {
          hasScrolledToUserMessageRef.current = true;
          scrollToLatestUserMessage();
        } else if (!isWaiting) {
          scrollToBottom();
        }
      });
    }
  }, [messages, isWaiting, scrollToLatestUserMessage, scrollToBottom]);

  return {
    chatWindowRef,
    messagesEndRef,
  };
};
